import React, { useEffect } from 'react'
import { Toaster } from '@center/ui-kit'
import { RecoilRoot } from 'recoil'
import { BrowserRouter as Router, useLocation } from 'react-router-dom'
import { ApolloProvider } from '@apollo/client'
import { ThemeProvider } from '@mui/material'
import CssBaseline from '@mui/material/CssBaseline'
import * as countries from 'i18n-iso-countries'
import * as Sentry from '@sentry/react'
import apolloClient from './apollo-client'
import { useSearchParams } from 'router'
import { useSetSite, useUser } from 'store'

import Routes from './routes'
import FakeSimulation from 'components/FooterSimulation'
import ErrorBoundary from 'components/ErrorBoundary/ErrorBoundary'
import './styles/variables.css'
import './styles/tailwind.css'
import './styles/fonts.css'
import './styles/styles.css'
import './styles/typeform-overrides.css'
import './styles/chrome.css'

import countryEn from 'i18n-iso-countries/langs/en.json'
import countryFr from 'i18n-iso-countries/langs/fr.json'
import countryIt from 'i18n-iso-countries/langs/it.json'
import countryDe from 'i18n-iso-countries/langs/de.json'
import countryEs from 'i18n-iso-countries/langs/es.json'
import ThemeCenter from './theme'

import ReactGA from 'react-ga4'
import InitializeGoogleAnalytics, { TrackPageView } from 'google-analytics/google-analytics-tracker-functionality'
import { initializeProductAnalytics, trackPageView } from 'analytics'

import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import i18next from 'i18next'

if (process.env.REACT_APP_SENTRY_DSN && process.env.NODE_ENV === 'production') {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.REACT_APP_ENV || 'production',
    sendDefaultPii: true,
    tracesSampleRate: 0.1,
    integrations: [],
    beforeSend(event, hint) {
      if (event.exception) {
        const error = hint.originalException;
        if (error && error.message && error.message.includes('ChunkLoadError')) {
          return null;
        }
      }
      return event;
    },
  });
}

countries.registerLocale(countryEn)
countries.registerLocale(countryFr)
countries.registerLocale(countryIt)
countries.registerLocale(countryDe)
countries.registerLocale(countryEs)

const App = () => {
  const location = useLocation()
  const [user] = useUser()
  const [searchParams] = useSearchParams()
  const setSite = useSetSite()
  
  useEffect(() => {
    if (searchParams.get('siteId')) {
      setSite(searchParams.get('siteId'))
    }
  }, [])

  useEffect(() => {
    if (!user || !process.env.REACT_APP_GA4_TRACKID) {
      return
    }

    InitializeGoogleAnalytics(user.user_id, user.email)
  }, [user])

  useEffect(() => {
    if (user?.user_id && process.env.NODE_ENV === 'production') {
      Sentry.setUser({
        id: user.user_id,
        email: user.email,
      });
    }
  }, [user])

  useEffect(() => {
    initializeProductAnalytics()
    trackPageView(location.pathname)
  }, [location.pathname])

  useEffect(() => {
    if (user?.user_id) {
      TrackPageView(location.pathname + location.search)
    } else {
      ReactGA.send({
        hitType: 'pageview',
        page: location.pathname,
      })
    }
  }, [location, user?.user_id])

  return (
    <ErrorBoundary>
      <Routes />
      <FakeSimulation user={user} />
    </ErrorBoundary>
  )
}

const Index = () => {
  return (
    <RecoilRoot>
      <ApolloProvider client={apolloClient}>
        <ThemeProvider theme={ThemeCenter}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={i18next.language}>
            <CssBaseline />
            <ErrorBoundary>
              <Router>
                <App />
                <Toaster position="top-right" />
              </Router>
            </ErrorBoundary>
          </LocalizationProvider>
        </ThemeProvider>
      </ApolloProvider>
    </RecoilRoot>
  )
}

export default Index
